import Message from '../components/Message';
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import { PageSkeleton } from '../components';
import { PageHeader } from '../components/layout';
import './ProfilePage.css';

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Jira 配置表单
  const [jiraConfig, setJiraConfig] = useState({
    jira_email: '',
    jira_api_token: ''
  });
  
  // 修改密码表单
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await api.getCurrentUser();
      setUser(response.data);
      
      // 获取 Jira 配置
      try {
        const jiraResponse = await api.getMyJiraConfig();
        setJiraConfig({
          jira_email: jiraResponse.data.jira_email || '',
          jira_api_token: '' // 不返回实际 token
        });
      } catch (err) {
        console.log('获取 Jira 配置失败');
      }
    } catch (err) {
      setMessage({ type: 'error', text: '获取用户信息失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleJiraConfigSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      await api.updateJiraConfig({
        jira_email: jiraConfig.jira_email || null,
        jira_api_token: jiraConfig.jira_api_token || null
      });
      setMessage({ type: 'success', text: 'Jira 配置已保存' });
      // 清空密码字段
      setJiraConfig(prev => ({ ...prev, jira_api_token: '' }));
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || '保存失败' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: '两次输入的新密码不一致' });
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      setMessage({ type: 'error', text: '新密码长度至少为6位' });
      return;
    }
    
    setSaving(true);
    
    try {
      await api.changePassword({
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      });
      setMessage({ type: 'success', text: '密码修改成功' });
      setPasswordForm({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || '密码修改失败' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <PageHeader title="账号设置" /><PageSkeleton rowCount={4} label="加载账号设置中" />
      </div>
    );
  }

  return (
    <div className="profile-page">
      <PageHeader
        title="账号设置"
      />

      <Message message={message.text} type={message.type} onClose={() => setMessage({ type: "", text: "" })} />

      {/* 用户信息卡片 */}
      <section className="configuration-settings-section profile-card">
        <div className="configuration-settings-section-header">
          <h3>基本信息</h3>
        </div>
        <div className="configuration-settings-section-body">
          <div className="profile-info">
            <div className="info-item">
              <label>用户名</label>
              <span>{user?.username}</span>
            </div>
            <div className="info-item">
              <label>邮箱</label>
              <span>{user?.email}</span>
            </div>
            <div className="info-item">
              <label>角色</label>
              <span className={`role-badge ${user?.is_admin ? 'admin' : 'user'}`}>
                {user?.is_admin ? '管理员' : '普通用户'}
              </span>
            </div>
            <div className="info-item">
              <label>Jira 凭证</label>
              <span className={`config-status ${user?.has_jira_config ? 'configured' : 'missing'}`}>
                {user?.has_jira_config ? '已配置' : '未配置'}
              </span>
            </div>
            <div className="info-item">
              <label>注册时间</label>
              <span>{user?.created_at ? new Date(user.created_at).toLocaleString() : '-'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Jira 配置卡片 */}
      <section className="configuration-settings-section profile-card">
        <div className="configuration-settings-section-header">
          <h3>Jira 凭证配置</h3>
          <span className="card-subtitle">
            配置个人 Jira 凭证后，您创建的规则将使用您的凭证调用 Jira API
          </span>
        </div>
        <div className="configuration-settings-section-body">
          <form onSubmit={handleJiraConfigSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Jira 邮箱</label>
                <input
                  type="email"
                  className="form-input"
                  value={jiraConfig.jira_email}
                  onChange={(e) => setJiraConfig({...jiraConfig, jira_email: e.target.value})}
                  placeholder="your-email@example.com"
                />
                <small className="form-help">
                  您的 Jira 账户邮箱地址
                </small>
              </div>
              
              <div className="form-group">
                <label className="form-label">Jira API Token</label>
                <input
                  type="password"
                  className="form-input"
                  value={jiraConfig.jira_api_token}
                  onChange={(e) => setJiraConfig({...jiraConfig, jira_api_token: e.target.value})}
                  placeholder={user?.has_jira_config ? '已配置（输入新值可修改）' : '请输入 API Token'}
                />
                <small className="form-help">
                  从 <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer">Atlassian 账户设置</a> 获取
                </small>
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? '保存中...' : '保存配置'}
              </button>
            </div>
          </form>
          
          <div className="config-note">
            <h4>配置说明：</h4>
            <ul>
              <li>若填写了个人 Jira 凭证，您创建的规则将<strong>优先使用您的凭证</strong>调用 Jira API</li>
              <li>若未配置个人凭证，将使用系统默认的 Jira 凭证</li>
              <li>您的 API Token 将加密存储，仅用于调用 Jira API</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 修改密码卡片 */}
      <section className="configuration-settings-section profile-card">
        <div className="configuration-settings-section-header">
          <h3>修改密码</h3>
        </div>
        <div className="configuration-settings-section-body">
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label className="form-label">当前密码</label>
              <input
                type="password"
                className="form-input"
                value={passwordForm.old_password}
                onChange={(e) => setPasswordForm({...passwordForm, old_password: e.target.value})}
                placeholder="请输入当前密码"
                required
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">新密码</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  placeholder="请输入新密码（至少6位）"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">确认新密码</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  placeholder="请再次输入新密码"
                  required
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? '修改中...' : '修改密码'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ProfilePage;
